import React, { useState, useEffect, useRef, useMemo } from "react";
import { getExpertises, getSubExpertises } from "../../../api/taxonomy";
import { useToast } from "../../../context/ToastContext";
import { X, Plus } from "lucide-react";
import ProfessionalRegistrationLayout from "./ProfessionalRegistrationLayout";
import CategoryPickerPopup from "../../common/CategoryPickerPopup";

const KNOWN_SUB_EXPERTISES = new Set([
  "Hostess",
  "Runner",
  "Registration",
  "Help Desk",
  "RSVP"
]);

export default function ProfessionalDetails({ onPrev, onNext, initialData = {} }) {
  const [expertiseOptions, setExpertiseOptions] = useState([]);
  const [selectedExpertise, setSelectedExpertise] = useState(initialData.rawExpertise || initialData.expertise || []);

  const [subExpertiseOptions, setSubExpertiseOptions] = useState([]);
  const [selectedSubExpertise, setSelectedSubExpertise] = useState(initialData.rawSubExpertise || initialData.subExpertise || []);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSubPopupOpen, setIsSubPopupOpen] = useState(false);
  const [expertiseSearch, setExpertiseSearch] = useState("");
  const [subExpertiseSearch, setSubExpertiseSearch] = useState("");
  const expertiseWrapperRef = useRef(null);
  const subExpertiseWrapperRef = useRef(null);
  const serviceRefs = useRef([]);

  const [services, setServices] = useState(
    initialData.services && initialData.services.length > 0
      ? initialData.services
      : [""]
  );
  const [additionalDetails, setAdditionalDetails] = useState(initialData.additionalDetails || "");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showError } = useToast();

  const MAX_EXPERTISE = 7;
  const MIN_EXPERTISE = 3;

  const isSubexpertise = (item) => {
    if (!item) return false;
    const name = typeof item === 'object' && item !== null ? item.name : String(item);
    if (KNOWN_SUB_EXPERTISES.has(name)) return true;
    return subExpertiseOptions.some(se => (typeof item === 'object' ? se._id === item._id : se.name === item));
  };

  // Fetch expertises based on role + selected categories
  useEffect(() => {
    const fetchExp = async () => {
      try {
        const role = initialData.professionalRole || initialData.purpose || "planner";
        const catIds = initialData.categoriesId || [];
        const expData = await getExpertises(role, catIds);
        if (expData.success) {
          setExpertiseOptions(expData.data);
        }
      } catch (error) {
        console.error("Error fetching expertises:", error);
      }
    };
    fetchExp();
  }, [initialData.categoriesId, initialData.professionalRole, initialData.purpose]);

  // Fetch sub-expertises when selected expertises change
  useEffect(() => {
    const fetchSubExp = async () => {
      try {
        const expIds = selectedExpertise.map(e => typeof e === 'object' ? e._id : e);
        if (expIds.length === 0) {
          setSubExpertiseOptions(prev => prev.filter(se => selectedSubExpertise.some(s => (typeof s === 'object' ? s._id : s) === (typeof se === 'object' ? se._id : se) || (typeof s === 'string' && s === se.name))));
          return;
        }
        const subData = await getSubExpertises(expIds);
        if (subData.success) {
          const fetched = subData.data || [];
          setSubExpertiseOptions(prev => {
            const map = new Map();
            prev.filter(se => selectedSubExpertise.some(s => (typeof s === 'object' ? s._id : s) === (typeof se === 'object' ? se._id : se) || (typeof s === 'string' && s === se.name))).forEach(se => map.set(se._id, se));
            fetched.forEach(se => map.set(se._id, se));
            return Array.from(map.values());
          });
        }
      } catch (error) {
        console.error("Error fetching sub-expertises:", error);
      }
    };
    fetchSubExp();
  }, [selectedExpertise, selectedSubExpertise]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expertiseWrapperRef.current && !expertiseWrapperRef.current.contains(event.target)) {
        setIsPopupOpen(false);
      }
      if (subExpertiseWrapperRef.current && !subExpertiseWrapperRef.current.contains(event.target)) {
        setIsSubPopupOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const role = initialData.professionalRole || initialData.purpose || "planner";

  const defaultServices = useMemo(() => {
    let list = [];
    if (role === "planner") {
      list = [
        "End-to-End Event Planning",
        "Vendor Coordination",
        "Timeline Planning",
        "Venue Management",
        "Logistics Management",
        "Production Management",
        "On-ground Operations",
        "Stage & AV Coordination",
        "Décor Coordination",
        "Catering Coordination",
        "Security Coordination",
        "Permissions & Compliance",
        "Artist Management"
      ];
    } else if (role === "performer" || role === "artist") {
      list = [
        "Singer",
        "Live Band",
        "DJ",
        "Dancer",
        "Emcee / Anchor",
        "Host/MC",
        "Instrumentalist",
        "Comedian",
        "Magician",
        "Kids Entertainer"
      ];
    } else if (role === "crew" || role === "volunteer") {
      list = [
        "Hostess",
        "Usher",
        "Runner",
        "Shadow",
        "Promoter",
        "Event Model",
        "Volunteers",
        "Registration",
        "Hospitality",
        "RSVP",
        "Event Coordination",
        "Help Desk"
      ];
    }

    return list.map(name => {
      const found = expertiseOptions.find(e => e.name === name) || subExpertiseOptions.find(se => se.name === name);
      if (found) return found;
      return { _id: name, name: name, isDefaultPlaceholder: true };
    });
  }, [role, expertiseOptions, subExpertiseOptions]);

  const visibleExpertises = useMemo(() => {
    const allSelected = [...selectedExpertise, ...selectedSubExpertise];
    const baseList = [...defaultServices];

    // Find custom selected items that are not in the default list
    const selectedCustomItems = allSelected.filter(sel => {
      const selName = typeof sel === 'object' && sel !== null ? sel.name : sel;
      return !baseList.some(d => d.name === selName);
    }).map(sel => {
      if (typeof sel === 'object' && sel !== null) return sel;
      return { _id: sel, name: sel };
    });

    const selectedList = baseList.filter(e =>
      allSelected.some(s => {
        const sName = typeof s === 'object' && s !== null ? s.name : s;
        const eName = e.name || e;
        const sId = typeof s === 'object' && s !== null ? s._id : s;
        const eId = e._id || e;
        return (sId && eId && String(sId) === String(eId)) || String(sName).toLowerCase() === String(eName).toLowerCase();
      })
    );
    const unselectedList = baseList.filter(e =>
      !allSelected.some(s => {
        const sName = typeof s === 'object' && s !== null ? s.name : s;
        const eName = e.name || e;
        const sId = typeof s === 'object' && s !== null ? s._id : s;
        const eId = e._id || e;
        return (sId && eId && String(sId) === String(eId)) || String(sName).toLowerCase() === String(eName).toLowerCase();
      })
    );

    let filteredUnselectedList = unselectedList;
    if (expertiseSearch) {
      const query = expertiseSearch.trim().toLowerCase();
      filteredUnselectedList = unselectedList.filter(e => {
        const name = e.name || "";
        return name.toLowerCase().includes(query);
      });
    }

    const defaultLength = defaultServices.length;
    const countToShow = Math.max(defaultLength, selectedList.length + selectedCustomItems.length);
    const combined = [...selectedList, ...selectedCustomItems, ...filteredUnselectedList];

    if (expertiseSearch) {
      return combined;
    }
    return combined.slice(0, countToShow);
  }, [defaultServices, selectedExpertise, selectedSubExpertise, expertiseSearch]);

  const selectedNamesLower = new Set(
    [...selectedExpertise, ...selectedSubExpertise].map(e => {
      const name = typeof e === 'object' && e !== null ? e.name : e;
      return String(name || "").trim().toLowerCase();
    })
  );

  const getTagName = (item) => {
    if (!item) return "";
    return typeof item === 'object' ? item.name || item.title || item.label || "" : String(item);
  };

  const query = expertiseSearch.trim().toLowerCase();

  const filteredExpertiseOptions = expertiseOptions
    .filter(exp => !selectedNamesLower.has(getTagName(exp).toLowerCase()))
    .filter(exp => {
      const name = getTagName(exp);
      if (!query) return true;
      return name.toLowerCase().includes(query);
    });

  const filteredSubExpertiseOptions = subExpertiseOptions
    .filter(exp => !selectedNamesLower.has(getTagName(exp).toLowerCase()))
    .filter(exp => {
      const name = getTagName(exp);
      if (!query) return true;
      return name.toLowerCase().includes(query);
    });

  const combinedPopupOptions = [...filteredExpertiseOptions, ...filteredSubExpertiseOptions];

  const handleCreateExpertiseSuggestion = (word) => {
    if (!word || !word.trim()) return;
    const name = word.trim();
    if (name.replace(/\s+/g, ' ').split(' ').length > 3) {
      showError("Suggestion can be at most 3 words.");
      return;
    }
    if (name.length > 35) {
      showError("Suggestion can be at most 35 characters.");
      return;
    }

    setSelectedExpertise((prev) => {
      const exists = prev.some(e => (typeof e === "string" ? e === name : e.name === name));
      if (exists) return prev;

      const totalSelected = prev.length + selectedSubExpertise.length;
      if (totalSelected >= MAX_EXPERTISE) {
        showError(`You can select a maximum of ${MAX_EXPERTISE} services offered.`);
        return prev;
      }
      return [...prev, name];
    });

    setExpertiseSearch("");
    setIsPopupOpen(false);
  };

  const handleCombinedPopupToggle = (item) => {
    const isSub = isSubexpertise(item);
    if (isSub) {
      toggleSubExpertise(item);
    } else {
      toggleExpertise(item);
    }
  };

  const toggleExpertise = (exp) => {
    const isCustomObj = typeof exp === "object" && exp !== null && exp.isCustom;
    const expName = isCustomObj ? exp.name : (typeof exp === "string" ? exp : exp.name);
    const expId = isCustomObj ? exp._id : (typeof exp === "string" ? exp : exp._id);

    setSelectedExpertise((prev) => {
      const exists = prev.some(e => {
        const eName = typeof e === "string" ? e : e.name;
        const eId = typeof e === "string" ? e : e._id;
        return eId === expId || eName === expName;
      });
      if (exists) {
        return prev.filter(e => {
          const eName = typeof e === "string" ? e : e.name;
          const eId = typeof e === "string" ? e : e._id;
          return eId !== expId && eName !== expName;
        });
      } else {
        const totalSelected = prev.length + selectedSubExpertise.length;
        if (totalSelected >= MAX_EXPERTISE) {
          showError(`You can select a maximum of ${MAX_EXPERTISE} services offered.`);
          return prev;
        }
        return [...prev, exp];
      }
    });
  };

  const toggleSubExpertise = (subExp) => {
    const isCustomObj = typeof subExp === "object" && subExp !== null && subExp.isCustom;
    const subExpName = isCustomObj ? subExp.name : (typeof subExp === "string" ? subExp : subExp.name);
    const subExpId = isCustomObj ? subExp._id : (typeof subExp === "string" ? subExp : subExp._id);

    setSelectedSubExpertise((prev) => {
      const exists = prev.some(e => {
        const eName = typeof e === "string" ? e : e.name;
        const eId = typeof e === "string" ? e : e._id;
        return eId === subExpId || eName === subExpName;
      });
      if (exists) {
        return prev.filter(e => {
          const eName = typeof e === "string" ? e : e.name;
          const eId = typeof e === "string" ? e : e._id;
          return eId !== subExpId && eName !== subExpName;
        });
      } else {
        const totalSelected = selectedExpertise.length + prev.length;
        if (totalSelected >= MAX_EXPERTISE) {
          showError(`You can select a maximum of ${MAX_EXPERTISE} services offered.`);
          return prev;
        }
        return [...prev, subExp];
      }
    });
  };



  const handleServiceChange = (index, value) => {
    const next = [...services];
    next[index] = value;
    setServices(next);
  };

  const handleServiceKeyDown = (e, idx) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Prevent adding if the current point is empty, or if there is already an empty point
      if (!services[idx]?.trim() || services.some(s => !s.trim())) {
        return;
      }
      const next = [...services.slice(0, idx + 1), "", ...services.slice(idx + 1)];
      setServices(next);
      setTimeout(() => serviceRefs.current[idx + 1]?.focus(), 0);
    } else if (e.key === "Backspace" && services[idx] === "" && services.length > 1) {
      e.preventDefault();
      const next = services.filter((_, i) => i !== idx);
      setServices(next);
      setTimeout(() => serviceRefs.current[Math.max(0, idx - 1)]?.focus(), 0);
    }
  };

  const addService = () => {
    // Prevent adding multiple empty points
    if (services.some(s => !s.trim())) {
      const emptyIdx = services.findIndex(s => !s.trim());
      if (emptyIdx !== -1) {
        serviceRefs.current[emptyIdx]?.focus();
      }
      return;
    }
    setServices((prev) => {
      const next = [...prev, ""];
      setTimeout(() => serviceRefs.current[next.length - 1]?.focus(), 0);
      return next;
    });
  };

  const removeService = (indexToRemove) => {
    if (services.length === 1) {
      setServices([""]);
      return;
    }
    setServices((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleNext = async () => {
    const totalSelected = selectedExpertise.length + selectedSubExpertise.length;
    if (totalSelected < MIN_EXPERTISE) {
      return showError(`Please select at least ${MIN_EXPERTISE} service categories (currently ${totalSelected} selected).`);
    }
    if (totalSelected > MAX_EXPERTISE) {
      return showError(`You can select a maximum of ${MAX_EXPERTISE} service categories.`);
    }

    const filteredServices = services.map(s => s.trim()).filter(Boolean);
    if (filteredServices.length === 0) {
      return showError("Please add at least one service highlight.");
    }
    if (!additionalDetails.trim()) {
      return showError("Please provide some additional information.");
    }

    setIsSubmitting(true);
    try {
      await onNext({
        expertise: selectedExpertise.map(e => typeof e === "object" && e !== null ? e.name : e),
        rawExpertise: selectedExpertise,
        subExpertise: selectedSubExpertise.map(e => typeof e === "object" && e !== null ? e.name : e),
        rawSubExpertise: selectedSubExpertise,
        services: filteredServices,
        additionalDetails,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ProfessionalRegistrationLayout
      title={initialData.accountType === "company" ? "Tell Me About Your Business" : "Tell Me About Yourself"}
      description="Help us understand your services so we can connect you with the right clients."
      currentRole={role}
      isRoleFrozen={true}
      onBack={() => {
        // Pass back any existing data so it isn't lost if they navigate
        onPrev({
          rawExpertise: selectedExpertise,
          expertise: selectedExpertise.map(e => typeof e === "object" && e !== null ? e.name : e),
          rawSubExpertise: selectedSubExpertise,
          subExpertise: selectedSubExpertise.map(e => typeof e === "object" && e !== null ? e.name : e),
          services,
          additionalDetails
        });
      }}
      onContinue={handleNext}
      continueLabel={isSubmitting ? "Submitting..." : "Submit"}
      isLoading={isSubmitting}
    >
      <div className="flex flex-col gap-5 h-full">

        {/* Expertise Section */}
        <div className="flex flex-col gap-2">
          <label className="text-base font-bold text-gray-900">
            Services Offered <span className="text-[#FF4C18]">*</span>
          </label>
          <div className="flex flex-wrap gap-2 relative" ref={expertiseWrapperRef}>
            {visibleExpertises.map((exp) => {
              const isActuallySub = isSubexpertise(exp) || selectedSubExpertise.some(e => typeof e === 'object' && e !== null ? e._id === exp._id || e.name === exp.name : e === exp._id || e === exp.name);

              const isSelected =
                selectedExpertise.some(e => typeof e === 'object' && e !== null ? e._id === exp._id || e.name === exp.name : e === exp._id || e === exp.name) ||
                selectedSubExpertise.some(e => typeof e === 'object' && e !== null ? e._id === exp._id || e.name === exp.name : e === exp._id || e === exp.name);

              const handleToggle = () => {
                if (isActuallySub) {
                  toggleSubExpertise(exp);
                } else {
                  toggleExpertise(exp);
                }
              };

              const totalSelected = selectedExpertise.length + selectedSubExpertise.length;
              const disabled = isSubmitting || (!isSelected && totalSelected >= MAX_EXPERTISE);

              return (
                <button
                  key={exp._id}
                  type="button"
                  onClick={handleToggle}
                  disabled={disabled}
                  className={`px-4 py-2 rounded-lg border text-sm transition-all whitespace-nowrap flex items-center justify-center gap-2 ${isSelected
                    ? "bg-[#FF4C18] text-white border-[#FF4C18] font-medium"
                    : "bg-white text-gray-700 border-gray-300 hover:border-[#FF4C18] hover:text-[#FF4C18] hover:bg-orange-50/50"
                    } ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {exp.name}
                </button>
              );
            })}

            <div className={`relative flex items-center ${isPopupOpen ? "z-50 w-full basis-full mt-1" : "z-10"}`}>
              {(() => {
                const totalSelected = selectedExpertise.length + selectedSubExpertise.length;
                const isLimitReached = totalSelected >= MAX_EXPERTISE;
                return (
                  <div className={`px-4 py-2 rounded-lg border border-dashed border-gray-300 transition-all flex items-center justify-center gap-1 ${isSubmitting || isLimitReached ? "opacity-50 cursor-not-allowed" : "hover:border-[#FF4C18] bg-white"}`}>
                    {!expertiseSearch && <Plus className="w-4 h-4 text-gray-400" />}
                    <input
                      type="text"
                      placeholder={!expertiseSearch ? "Add More" : ""}
                      value={expertiseSearch}
                      onChange={(e) => {
                        setExpertiseSearch(e.target.value);
                        if (!isPopupOpen) setIsPopupOpen(true);
                      }}
                      onClick={() => setIsPopupOpen(true)}
                      disabled={isSubmitting || isLimitReached}
                      className="bg-transparent border-none outline-none text-center text-sm text-gray-700 w-24 placeholder-gray-500 transition-all"
                    />
                  </div>
                );
              })()}

              <CategoryPickerPopup
                variant="dropdown"
                isOpen={isPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                options={combinedPopupOptions}
                selectedIds={[
                  ...selectedExpertise.map(e => typeof e === 'object' ? e._id : e),
                  ...selectedSubExpertise.map(e => typeof e === 'object' ? e._id : e)
                ]}
                selectedNames={[
                  ...selectedExpertise.map(e => typeof e === 'object' && e !== null ? e.name : String(e)),
                  ...selectedSubExpertise.map(e => typeof e === 'object' && e !== null ? e.name : String(e))
                ]}
                onToggle={handleCombinedPopupToggle}
                title="Select Services"
                searchTerm={expertiseSearch}
                onCreateSuggestion={handleCreateExpertiseSuggestion}
              />
            </div>
          </div>
        </div>

        {/* Services Offered / Highlights */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-gray-900">
            Highlights <span className="text-[#FF4C18]">*</span>
          </label>
          <div
            className="border border-gray-200 rounded-xl p-3 h-[140px] overflow-y-auto flex flex-col justify-between bg-white shadow-sm hide-scrollbar [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <div className="flex flex-col gap-2 text-sm text-gray-800">
              {services.map((service, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <span className="text-[#FF4C18] text-base leading-snug select-none mt-0.5">•</span>
                  <textarea
                    ref={(el) => {
                      serviceRefs.current[idx] = el;
                      if (el) {
                        el.style.height = "auto";
                        el.style.height = el.scrollHeight + "px";
                      }
                    }}
                    rows={1}
                    value={service}
                    onChange={(e) => {
                      e.target.style.height = "auto";
                      e.target.style.height = e.target.scrollHeight + "px";
                      handleServiceChange(idx, e.target.value);
                    }}
                    onKeyDown={(e) => handleServiceKeyDown(e, idx)}
                    placeholder={idx === 0 ? "Type a service (e.g. Stage Management)…" : "Type here…"}
                    className="flex-1 outline-none bg-transparent text-sm text-gray-800 py-0.5 placeholder-gray-400 border-none focus:outline-none resize-none overflow-hidden leading-snug break-words"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                  />
                  {services.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeService(idx)}
                      className="text-gray-400 hover:text-red-500 p-1 transition-colors flex-shrink-0 mt-0.5"
                      title="Remove item"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="pt-1">
              <button
                type="button"
                onClick={addService}
                className="text-xs text-[#FF4C18] hover:text-orange-600 flex items-center gap-1 mt-1 font-medium transition-colors cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" /> Add item
              </button>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-gray-900">
            Additional Information <span className="text-[#FF4C18]">*</span>
          </label>
          <textarea
            value={additionalDetails}
            onChange={(e) => setAdditionalDetails(e.target.value)}
            placeholder="Write your additional information"
            rows={4}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#FF4C18] resize-none"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          />
        </div>

      </div>

    </ProfessionalRegistrationLayout>
  );
}
